import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, ArrowRight, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ActivateYear() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestedYear = parseInt(searchParams.get("tax_year") || `${new Date().getFullYear()}`, 10);
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tax-year-entitlements/${requestedYear}`, { withCredentials: true });
        setEntitlement(data);
      } catch (err) {
        // 404 — no entitlement yet. Display as "needs provisioning" — should not normally happen
        // because backend auto-provisions on login.
        setEntitlement(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [requestedYear]);

  const doActivate = async () => {
    setActivating(true);
    try {
      const { data } = await axios.post(
        `${API}/year-end/activate?tax_year=${requestedYear}`,
        {},
        { withCredentials: true }
      );
      setEntitlement(data);
      toast.success(`Tax Year ${requestedYear} activated`);
      // Redirect to dashboard after a short pause so user sees confirmation
      setTimeout(() => navigate("/app/dashboard"), 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "subscription_required") {
        toast.error(detail.message);
        navigate("/app/pricing");
      } else {
        toast.error(detail?.message || "Activation failed");
      }
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-slate-500"><Loader2 className="inline w-6 h-6 animate-spin" /> Loading…</div>;
  }

  if (entitlement?.entitlement_state === "active") {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="activate-already-active">
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-8 text-center">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-700 mb-3" />
          <h1 className="text-2xl font-bold text-emerald-900">Tax Year {requestedYear} is already active</h1>
          <button onClick={() => navigate("/app/dashboard")} className="mt-6 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl mx-auto" data-testid="activate-year-page">
      <div className="bg-white border-2 border-indigo-200 rounded-xl p-8 text-center">
        <Calendar className="w-12 h-12 mx-auto text-indigo-700 mb-3" />
        <h1 className="text-3xl font-bold text-slate-900">Activate Tax Year {requestedYear}</h1>
        <p className="text-slate-600 mt-3">
          Activating this workspace gives you a fresh year for transactions, equipment, CACFP allocations, and reports.
          Your add-on page balance (never expires) and your equipment depreciation schedules carry forward automatically.
        </p>
        {entitlement?.entitlement_state === "inactive" && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-xs text-amber-800">
            <AlertTriangle className="w-3 h-3" /> Currently inactive — activate to begin
          </div>
        )}
        <button
          data-testid="confirm-activate-btn"
          onClick={doActivate}
          disabled={activating || entitlement?.entitlement_state === "active"}
          className="mt-6 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded-lg font-medium"
        >
          {activating ? <><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Activating…</> : <>Activate {requestedYear} <ArrowRight className="inline w-4 h-4 ml-2" /></>}
        </button>
      </div>
    </div>
  );
}
